import { Card, Typography, Table, Button, InputNumber, Divider } from "antd";
import { useContext, useState, useMemo } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";

const { Title } = Typography;

export default function SalaryCalculation({ salary_details = {} }) {
  const { backUrl, authData } = useContext(AuthContext);

  // Helper: coerce various inputs (strings with commas, null, etc.) into numbers
  const toNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v == null) return 0;
    // Remove commas and any non-digit except minus/dot, then parse
    const cleaned = String(v).replace(/,/g, "").replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  // Ensure initial editableData stores numeric values for numeric fields
  const normalizedInit = Object.entries(salary_details).reduce((acc, [k, val]) => {
    acc[k] = toNumber(val);
    return acc;
  }, {});
  const [editableData, setEditableData] = useState({ ...normalizedInit });
  const [overtimeHours, setOvertimeHours] = useState(0);

  // Assume 26 working days × 8 hours/day = 208 hours in a month
  const hourlyRate = useMemo(() => {
    const net = toNumber(editableData.net);
    return net / 208 || 0;
  }, [editableData.net]);

  const overtimePay = useMemo(() => {
    const hrs = toNumber(overtimeHours);
    return hrs * hourlyRate;
  }, [overtimeHours, hourlyRate]);

  const finalSalary = useMemo(() => {
    return toNumber(editableData.net) + overtimePay;
  }, [editableData.net, overtimePay]);

  const handleValueChange = (value, key) => {
    // InputNumber onChange may give number or null; coerce to number
    const numeric = toNumber(value);
    setEditableData((prev) => ({
      ...prev,
      [key]: numeric,
    }));
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editableData,
        overtime_hours: toNumber(overtimeHours),
        overtime_pay: Number(overtimePay.toFixed(2)),
        final_salary: Number(finalSalary.toFixed(2)),
      };

      await axios.patch(`${backUrl}/api/payroll/drafts/${salary_details.id}/`, payload, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });

      alert("Salary data (including overtime) sent to backend successfully");
    } catch (error) {
      console.error("Error sending salary data to backend:", error);
      alert("Failed to send salary data to backend");
    }
  };

  const dataSource = [
    { key: "1", component: "Basic", keyName: "basic" },
    { key: "2", component: "HRA", keyName: "hra" },
    { key: "3", component: "Conveyance", keyName: "conveyance" },
    { key: "4", component: "Allowances", keyName: "others" },
    { key: "5", component: "Gross Wages", keyName: "gross" },
    { key: "6", component: "PF (Employer)", keyName: "pf" },
    { key: "7", component: "ESI (Employer)", keyName: "esi" },
    { key: "8", component: "Net Take Home", keyName: "net" },
  ];

  const columns = [
    {
      title: "Component",
      dataIndex: "component",
      key: "component",
      width: "30%",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Monthly (₹)",
      key: "monthly",
      align: "center",
      width: "30%",
      render: (_, record) => (
        <InputNumber
          value={toNumber(editableData[record.keyName])}
          min={0}
          style={{ width: "70%" }}
          onChange={(value) => handleValueChange(value, record.keyName)}
          formatter={(value) =>
            value || value === 0 ? toNumber(value).toLocaleString("en-IN") : "0"
          }
          parser={(value) => String(value).replace(/\D/g, "")}
        />
      ),
    },
    {
      title: "Yearly (₹)",
      key: "yearly",
      align: "center",
      width: "30%",
      render: (_, record) => {
        const monthly = toNumber(editableData[record.keyName]);
        const yearly = monthly * 12;
        return yearly > 0 ? yearly.toLocaleString("en-IN") : "0";
      },
    },
  ];

  return (
    <Card className="p-4 shadow-md rounded-xl">
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <Title level={4}>SALARY STRUCTURE</Title>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        rowClassName={(record) =>
          record.component === "Net Take Home"
            ? "ant-table-row-selected"
            : record.component === "Gross Wages"
            ? "ant-table-row-hover"
            : ""
        }
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{ backgroundColor: "#0e497b", color: "white" }}
              >
                {props.children}
              </th>
            ),
          },
        }}
      />

      {/* ---------- Overtime Section ---------- */}
      <Divider />
      {/* <div style={{ marginTop: "20px" }}>
        <Title level={5}>Overtime Calculation</Title>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <span>Overtime Hours:</span>
          <InputNumber
            min={0}
            value={toNumber(overtimeHours)}
            onChange={(val) => setOvertimeHours(toNumber(val))}
            precision={2}
            style={{ width: 120 }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <b>Hourly Rate:</b> ₹{Number(hourlyRate || 0).toFixed(2)}
        </div>

        <div style={{ marginBottom: 8 }}>
          <b>Overtime Pay:</b> ₹{Number(overtimePay || 0).toLocaleString("en-IN")}
        </div>

        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            marginTop: 12,
            color: "#0e497b",
          }}
        >
          Final Salary for the Month: ₹{Number(finalSalary || 0).toLocaleString("en-IN")}
        </div>
      </div> */}

      {/* ---------- Submit Button ---------- */}
      <div style={{ textAlign: "center", marginTop: "24px" }}>
        <Button type="primary" onClick={handleEditSubmit}>
          Submit
        </Button>
      </div>
    </Card>
  );
}
